import * as React from 'react'
import { useEffect } from 'react'
import { Button } from '@/components/ui/button'
import {
  NavigationMenu,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
} from '@/components/ui/navigation-menu'
import AppNavbarLink from './app-navbar-link'
import NavbarDropdownMenu from './navbar-dropdown-menu'
import NavbarLogo from './navbar-logo'
import NavbarCartButton from './navbar-cart-button'
import NavbarUserMenu from './navbar-user-menu'
import {
  adminItems,
  adventuresItems,
  advisorItems,
  storeItems,
  superadminItems,
} from '../../config/navbar-data'
import { useUserStore } from '../../stores/user-store'
import {
  filterNavbarSections,
  shouldShowAdminMenu,
  shouldShowAdvisorMenu,
  shouldShowMemberCTA,
} from '../../lib/navigation-utils'

export default function DesktopNavbar() {
  const [value, setValue] = React.useState('')
  const { user, tenant, fetchUserData, fetchCartCount } = useUserStore()

  useEffect(() => {
    fetchUserData()
    fetchCartCount()
  }, [])

  const filteredAdventures = filterNavbarSections(adventuresItems, user, tenant)
  const filteredStore = filterNavbarSections(storeItems, user, tenant)
  const filteredAdvisor = filterNavbarSections(advisorItems, user, tenant)
  const filteredAdmin = filterNavbarSections(adminItems, user, tenant)
  const filteredSuperadmin = filterNavbarSections(superadminItems, user, tenant)

  const showAdvisor = shouldShowAdvisorMenu(user)
  const showAdmin = shouldShowAdminMenu(user)
  const showSuperadmin = user?.superadmin === true
  const showMemberCTA = shouldShowMemberCTA(user, tenant)
  const showHuntPlanCTA = tenant?.hunts_enabled && !tenant?.is_store_only

  return (
    <div className="w-full">
      <div className="w-full px-6">
        <div className="flex items-center justify-between" style={{ minHeight: '117px' }}>
          <NavbarLogo height={80} />

          <div className="ml-auto flex items-center">
            {user
              ? (
                  <>
                    <NavigationMenu value={value} onValueChange={setValue} viewport={false}>
                      <NavigationMenuList>
                        <NavigationMenuItem>
                          <NavigationMenuLink asChild>
                            <AppNavbarLink href="/dashboard" variant="default" className="h-9">
                              Dashboard
                            </AppNavbarLink>
                          </NavigationMenuLink>
                        </NavigationMenuItem>

                        {filteredAdventures.length > 0 && (
                          <NavbarDropdownMenu
                            title="Adventures"
                            sections={filteredAdventures}
                            value="adventures"
                          />
                        )}

                        {filteredStore.length > 0 && (
                          <NavbarDropdownMenu
                            title="Store"
                            sections={filteredStore}
                            value="store"
                          />
                        )}

                        {showAdvisor && (
                          <NavbarDropdownMenu
                            title="Advisor"
                            sections={filteredAdvisor}
                            value="advisor"
                          />
                        )}

                        {showAdmin && (
                          <NavbarDropdownMenu
                            title="Admin"
                            sections={[
                              ...filteredAdmin,
                              ...(showSuperadmin ? filteredSuperadmin : []),
                            ]}
                            value="admin"
                          />
                        )}
                      </NavigationMenuList>
                    </NavigationMenu>

                    <div className="flex items-center space-x-3">
                      <div className="space-x-4 px-12">
                        <NavbarCartButton />
                        <NavbarUserMenu />
                      </div>

                      {showMemberCTA && (
                        <Button asChild variant="outline" className="h-11 px-4 py-3.5">
                          <a href="/membership_pricing" className="font-sans text-sm normal-case">Become a member</a>
                        </Button>
                      )}

                      {showHuntPlanCTA && user.member && (
                        <Button asChild variant="default" className="h-11 px-4 py-3.5">
                          <a href="/hunt_plans/new" className="font-sans text-sm !text-white normal-case">Plan Your Hunt</a>
                        </Button>
                      )}
                    </div>
                  </>
                )
              : (
                  <div className="flex items-center space-x-3">
                    {tenant?.show_shopping_cart && (
                      <NavbarCartButton />
                    )}

                    <Button asChild variant="ghost" className="h-11 px-4 py-3.5">
                      <a href="/login" className="font-sans text-sm normal-case">Login</a>
                    </Button>

                    {tenant?.terms_of_service_url && (
                      <Button asChild variant="ghost" className="h-11 px-4 py-3.5">
                        <a href={tenant.terms_of_service_url} target="_blank" rel="noopener noreferrer" className="font-sans text-sm normal-case">
                          Terms of Service
                        </a>
                      </Button>
                    )}
                  </div>
                )}
          </div>
        </div>
      </div>
    </div>
  )
}
